import { InfoCircleOutlined } from "@ant-design/icons"
import { Button, DatePicker, type DatePickerProps, Form, Input, InputNumber, Select, Tooltip } from "antd"
import locale from "antd/es/date-picker/locale/pt_BR"
import { MaskedInput } from "antd-mask-input"
import { type MaskType } from "antd-mask-input/build/main/lib/MaskedInput"
import countryCodes, { type CountryProperty } from "country-codes-list"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"

import { onRequiredTypeChange, removePunctuation, toLicensePlate } from "../../../helpers/format"
import validators from "../../../helpers/validators"
import { type ApplicationState } from "../../../store"

const { Option } = Select

type CountryPhoneCode = { code: string, country: string }
export interface PersonalInfoData {
  email: string
  name: string
  cpf: string
  clientPhoneCountry: CountryPhoneCode
  clientPhone: string
  company: string
  licensePlate: string
  pickupDate: Dayjs
  carKm: number
  model: string
  dealer: string
  consultantEmail: string
  consultantName: string
}

type OwnProps = {
  onPlateChange?: (plate: string) => void
  afterFinish?: (formData: PersonalInfoData) => void
  defaults?: ApplicationState["onefleetentrega"]
}

const PersonalInfo = (props: OwnProps): React.ReactElement => {
  const [formData] = Form.useForm()
  const [plateChanged, setPlateChanged] = useState<boolean>(false)
  const [clientPhoneCountry, setClientPhoneCountry] = useState<CountryPhoneCode>(props.defaults?.data.clientPhoneCountry ?? { code: "55", country: "+55 Brasil" })

  const plateMask = React.useMemo<MaskType>(() => {
    return [{ mask: "aaa-0*00" }]
  }, [])

  const range = (start: number, end: number) => {
    const result = []
    for (let i = start; i < end; i++) {
      result.push(i)
    }
    return result
  }

  const disabledDate: DatePickerProps["disabledDate"] = (current) => {
    return current > dayjs()
  }

  const disabledDateTime: any = (current: any) => {
    const isToday = dayjs().diff(current, "d") === 0
    return {
      disabledHours: () => isToday ? range(dayjs().add(1, "h").hour(), 24) : [],
      disabledMinutes: () => isToday ? range(dayjs().add(1, "m").minute(), 60) : []
    }
  }

  function handleFormFinish () {
    if (props.afterFinish) {
      const {
        email,
        name,
        cpf,
        clientPhone,
        company,
        licensePlate,
        pickupDate,
        carKm,
        model,
        dealer,
        consultantName,
        consultantEmail
      } = formData.getFieldsValue() as PersonalInfoData

      props.afterFinish({
        email,
        name,
        cpf: removePunctuation(cpf),
        clientPhone,
        company,
        licensePlate: toLicensePlate(licensePlate),
        pickupDate,
        carKm,
        model,
        dealer,
        consultantName,
        consultantEmail,
        clientPhoneCountry
      })
    }
  }

  useEffect(() => {
    if (props.defaults?.plateInfo?.data && plateChanged) {
      const {
        carKm,
        model
      } = props.defaults.plateInfo.data

      formData.setFieldsValue({
        carKm,
        model
      })

      setPlateChanged(false)
    }
  }, [props.defaults?.plateInfo?.data])

  const handlePhoneChange = () => {
    const value = formData.getFieldValue("clientPhone")

    if (value) {
      let initialValue = value.replace(/\D/g, "")
      if (initialValue.length > 11) initialValue = initialValue.replace(/(?<=\d{11})\d?/g, "")

      let formated = initialValue

      if (clientPhoneCountry.code === "55") {
        formated = formated.replace(/^(\d{0,2})/, "($1) ")

        if (initialValue.length < 11) formated = formated.replace(/(?<=\(\d{2}\) )(\d{4})(?=\d)/, "$1-")
        else if (initialValue.length === 11) {
          formated = formated
            .replace(/(?<=\(\d{2}\) )(\d)/, "$1 ")
            .replace(/(?<=\(\d{2}\) \d )(\d{0,4})/, "$1-")
        }
      }

      formData.setFieldValue("clientPhone", formated)
    }
  }

  useEffect(() => { handlePhoneChange() }, [clientPhoneCountry])

  const countryCodeList = countryCodes.customList("countryCallingCode" as CountryProperty, "+{countryCallingCode} {countryNameLocal}")

  const selectCountry = (
    <Select
      defaultValue={clientPhoneCountry.country}
      onChange={(value) => { setClientPhoneCountry(JSON.parse(value)) }}
      showSearch
      style={{ maxWidth: "20vw" }}
    >
      {
        Object.entries(countryCodeList).map(([key, value]) => (<Option key={key} value={JSON.stringify({ code: key, country: value })}>{value}</Option>))
      }
    </Select>
  )

  return (
    <Form
      onFinish={handleFormFinish}
      form={formData}
      layout="vertical"
      initialValues={props.defaults?.data}
      requiredMark={onRequiredTypeChange}
    >
      <Form.ErrorList />

      <Form.Item
        name="name"
        label="Nome completo do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          { pattern: /\w+ (?:\w+)+/, message: "Favor informar nome completo!" },
          { pattern: /^[a-zA-Z\u00C0-\u00FF ]+$/i, message: "Nome Inválido!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Nome Completo do Representante"
        />
      </Form.Item>

      <Form.Item
        name="cpf"
        label="CPF do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          {
            validator: async (_, value) => {
              if (value && !validators.cpf(value)) throw new Error("CPF Inválido!")
            }
          }
        ]}
      >
        <MaskedInput
          placeholder="CPF do Representante"
          size="large"
          mask="000.000.000-00"
        />
      </Form.Item>

      <Form.Item
        name="email"
        label="E-mail do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          { pattern: /^[-_.\w]+@[-_.\w]+\.(?:[-_.\w]+\.?)+[-_.\w]$/, message: "E-mail inválido!" }
        ]}
      >
        <Input
          size="large"
          placeholder="E-mail do Representante"
        />
      </Form.Item>

      <Form.Item
        name="clientPhone"
        label="Telefone do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Telefone do Representante"
          onChange={handlePhoneChange}
          addonBefore={selectCountry}>
        </Input>
      </Form.Item>

      <Form.Item
        name="company"
        label="Nome da empresa"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Nome da empresa"
        />
      </Form.Item>

      <Form.Item
        name="licensePlate"
        label="Placa do veículo"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <MaskedInput
          size="large"
          placeholder="Placa do veículo"
          prefix={props.defaults?.plateInfo?.error && <Tooltip title={`Preenchimento manual necessário\nMotivo: ${props.defaults?.plateInfo?.message}`}><InfoCircleOutlined /></Tooltip>}
          mask={plateMask}
          onBlur={(e) => {
            if (props.onPlateChange) props.onPlateChange(removePunctuation(e.target.value).toUpperCase())
            setPlateChanged(true)
          }}
        />
      </Form.Item>

      <Form.Item
        name="model"
        label="Modelo do veículo"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <Input
          size="large"
          disabled={Boolean(props.defaults?.plateInfo?.loading)}
          placeholder="Modelo do veículo"
        />
      </Form.Item>

      <Form.Item
        name="carKm"
        label="Km do veículo"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <InputNumber
          size="large"
          disabled={Boolean(props.defaults?.plateInfo?.loading)}
          placeholder="Km do veículo"
          style={{ width: "100%" }}
          min={0}
          max={999999}
        />
      </Form.Item>

      <Form.Item
        name="dealer"
        label="Concessionária"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Concessionária"
        />
      </Form.Item>

      <Form.Item
        name="consultantName"
        label="Nome do consultor"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          { pattern: /\w+ (?:\w+)+/, message: "Favor informar nome completo!" },
          { pattern: /^[a-zA-Z\u00C0-\u00FF ]+$/i, message: "Nome Inválido!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Nome do consultor"
        />
      </Form.Item>

      <Form.Item
        name="consultantEmail"
        label="E-mail do consultor"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          { pattern: /^[-_.\w]+@[-_.\w]+\.(?:[-_.\w]+\.?)+[-_.\w]$/, message: "E-mail inválido!" }
        ]}
      >
        <Input
          size="large"
          placeholder="E-mail do consultor"
        />
      </Form.Item>

      <Form.Item
        name="pickupDate"
        label="Data de retirada"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <DatePicker
          showTime
          showSecond={false}
          disabledDate={disabledDate}
          disabledTime={disabledDateTime}
          format="DD/MM/YYYY HH:mm"
          placeholder="Data de retirada"
          size="large"
          style={{ width: "100%" }}
          locale={locale}
        />
      </Form.Item>

      <Form.Item label="">
        <Button htmlType="submit">Próximo</Button>
      </Form.Item>
    </Form>
  )
}

export default PersonalInfo
